<template>
  <v-card class="main-card mb-6" elevation="0">
    <v-card-title class="section-title">
      <v-icon left color="primary">mdi-hand-heart-outline</v-icon>
      Before you go
    </v-card-title>

    <v-card-text class="pa-6">
      <!-- Everything done -->
      <template v-if="finished">
        <v-alert v-if="donationSuccess || sponsorshipSuccess" type="success" variant="tonal" class="mb-0">
          <strong>Thank you.</strong>
          <span v-if="donationSuccess"> Your donation to {{ fund.title }} has been processed.</span>
          <span v-if="sponsorshipSuccess"> Your monthly sponsorship of ${{ sponsorshipAmount }} is set up — thank you for helping keep this memory alive.</span>
        </v-alert>
        <p v-else class="text-body-1 mb-0">
          Thank you for sharing your memory of {{ personName }}.
        </p>
      </template>

      <template v-else>
        <!-- ── Question 1: charity ── -->
        <div v-if="fund" class="support-question mb-6">
          <p class="text-body-1 mb-2">
            <strong>{{ personName }}</strong> is raising money for <strong>{{ fund.title }}</strong>.
            Would you like to donate?
          </p>
          <p v-if="fund.charityDescription" class="text-body-2 text--secondary mb-4">
            {{ fund.charityDescription }}
          </p>

          <div v-if="charityAnswer === null" class="support-actions">
            <v-btn color="primary" class="mr-2 mb-2" @click="charityAnswer = true">
              Yes, donate
            </v-btn>
            <v-btn color="primary" variant="outlined" class="mb-2" @click="declineCharity">
              Not this time
            </v-btn>
          </div>

          <template v-else-if="charityAnswer">
            <div class="preset-amounts mb-4">
              <v-btn
                v-for="preset in presetAmounts"
                :key="preset"
                :variant="selectedPreset === preset ? 'flat' : 'outlined'"
                :color="selectedPreset === preset ? 'primary' : 'default'"
                class="preset-btn mr-2 mb-2"
                rounded="0"
                @click="selectPreset(preset)"
              >
                {{ preset === 'other' ? 'Other' : `$${preset}` }}
              </v-btn>
            </div>

            <v-text-field
              v-model="donationAmountRaw"
              label="Amount (AUD)"
              variant="outlined"
              type="number"
              min="1"
              step="1"
              prefix="$"
              density="comfortable"
              class="mb-2"
              style="max-width: 200px;"
            />

            <v-checkbox
              v-model="donateAnonymously"
              label="Donate anonymously (your name won't be shown publicly)"
              density="comfortable"
              class="mb-0"
            />
          </template>

          <p v-else class="text-body-2 text--secondary mb-0">
            No problem — thank you for your memory.
          </p>
        </div>

        <!-- ── Question 2: sponsorship ── -->
        <div v-if="showSponsorshipQuestion" class="support-question">
          <v-divider v-if="fund" class="mb-5"></v-divider>

          <p class="text-body-1 mb-2">
            Would you like to sponsor {{ personName }}'s online memory for
            ${{ sponsorshipAmount }} a month (after the first year)?
          </p>

          <p class="text-body-2 text--secondary mb-3">
            Your ${{ sponsorshipAmount }} is shared with other sponsors to cover the $12 monthly hosting cost.
            If this memory is already fully subscribed, your sponsorship is passed on to
            <template v-if="fund"><strong>{{ fund.title }}</strong></template>
            <template v-else>our default charity</template>
            instead. You may cancel at any point.
          </p>

          <div v-if="sponsorAnswer === null" class="support-actions">
            <v-btn color="primary" class="mr-2 mb-2" @click="sponsorAnswer = true">
              Yes, sponsor
            </v-btn>
            <v-btn color="primary" variant="outlined" class="mb-2" @click="declineSponsorship">
              No thanks
            </v-btn>
          </div>
        </div>

        <!-- ── Payment ── -->
        <div v-if="needsPayment" class="mt-5">
          <label class="text-body-2 text--secondary mb-2 d-block">Card details</label>
          <div ref="cardMount" class="stripe-card-element"></div>
          <p v-if="stripeError" class="text-caption text-error mt-2">{{ stripeError }}</p>

          <v-alert v-if="donationError" type="error" variant="tonal" class="mt-4 mb-0">{{ donationError }}</v-alert>
          <v-alert v-if="sponsorshipError" type="error" variant="tonal" class="mt-4 mb-0">{{ sponsorshipError }}</v-alert>

          <v-btn
            color="primary"
            size="large"
            rounded="0"
            elevation="0"
            class="px-8 mt-4"
            :loading="processing"
            :disabled="processing || !canSubmit"
            @click="processSupport"
          >
            {{ submitLabel }}
          </v-btn>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'

const props = defineProps({
  slug: {
    type: String,
    required: true
  },
  personName: {
    type: String,
    default: 'this memory'
  },
  fund: {
    type: Object,
    default: null
  }
})

const store = useMemorialStore()

const sponsorshipAmount = 4
const presetAmounts = [10, 20, 50, 100, 'other']

// null = not answered yet, true = yes, false = declined
const charityAnswer = ref(null)
const sponsorAnswer = ref(null)

const selectedPreset = ref(20)
const donationAmountRaw = ref('20')
const donateAnonymously = ref(false)

const processing = ref(false)
const stripeError = ref('')
const donationError = ref('')
const sponsorshipError = ref('')
const donationSuccess = ref(false)
const sponsorshipSuccess = ref(false)
const declinedEverything = ref(false)

const cardMount = ref(null)
let stripeInstance = null
let cardElement = null

// The sponsorship question follows the charity one; with no fund it is the only question
const showSponsorshipQuestion = computed(() => {
  return !props.fund || charityAnswer.value !== null
})

const donationAmountValid = computed(() => {
  const value = parseFloat(donationAmountRaw.value)
  return !isNaN(value) && value >= 1
})

const needsPayment = computed(() => {
  return charityAnswer.value === true || sponsorAnswer.value === true
})

const canSubmit = computed(() => {
  if (charityAnswer.value === true && !donationAmountValid.value) return false
  // The sponsorship question must have been answered before anything is charged
  return sponsorAnswer.value !== null
})

const finished = computed(() => {
  return donationSuccess.value || sponsorshipSuccess.value || declinedEverything.value
})

const submitLabel = computed(() => {
  if (charityAnswer.value === true && sponsorAnswer.value === true) return 'Donate and Sponsor'
  if (charityAnswer.value === true) return `Donate $${donationAmountDisplay.value} AUD`
  return `Sponsor $${sponsorshipAmount} a month`
})

const donationAmountDisplay = computed(() => {
  const value = parseFloat(donationAmountRaw.value)
  return isNaN(value) ? '0' : value.toFixed(2)
})

const selectPreset = (preset) => {
  selectedPreset.value = preset
  if (preset !== 'other') {
    donationAmountRaw.value = String(preset)
  }
}

const declineCharity = () => {
  charityAnswer.value = false
}

const declineSponsorship = () => {
  sponsorAnswer.value = false
  // Nothing was accepted, so there is nothing left to ask
  if (charityAnswer.value !== true) {
    declinedEverything.value = true
  }
}

const initStripe = async () => {
  await nextTick()
  if (cardElement || !cardMount.value) return

  const key = process.env.VUE_APP_STRIPE_PUBLIC_KEY
  if (!key) return

  stripeInstance = await loadStripe(key)
  if (!stripeInstance) return

  const elements = stripeInstance.elements()
  cardElement = elements.create('card', {
    hidePostalCode: true,
    style: {
      base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } }
    }
  })
  cardElement.mount(cardMount.value)
  cardElement.on('change', (event) => {
    stripeError.value = event.error ? event.error.message : ''
  })
}

const processSupport = async () => {
  if (!cardElement || !stripeInstance || processing.value) return

  processing.value = true
  donationError.value = ''
  sponsorshipError.value = ''

  try {
    // Create the PaymentMethod once — reused for both donation and sponsorship
    const { error: pmError, paymentMethod } = await stripeInstance.createPaymentMethod({
      type: 'card',
      card: cardElement
    })
    if (pmError) {
      donationError.value = pmError.message || 'Card error. Please check your details.'
      return
    }

    if (charityAnswer.value === true) {
      const donationResponse = await apiService.processDonation({
        memorialSlug: props.slug,
        amount: parseFloat(donationAmountRaw.value),
        anonymous: donateAnonymously.value,
        paymentMethodId: paymentMethod.id
      }, store.apiToken)

      if (donationResponse.success) {
        donationSuccess.value = true
      } else {
        donationError.value = donationResponse.msg || 'Payment failed. Please try again.'
        return
      }
    }

    if (sponsorAnswer.value === true) {
      const sponsorshipResponse = await apiService.createSponsorship({
        memorialSlug: props.slug,
        paymentMethodId: paymentMethod.id
      }, store.apiToken)

      if (sponsorshipResponse.success) {
        sponsorshipSuccess.value = true
      } else if (sponsorshipResponse.requiresAction && sponsorshipResponse.clientSecret) {
        // 3D Secure required — ask Stripe to confirm on the client side
        const { error: confirmError } = await stripeInstance.confirmCardPayment(sponsorshipResponse.clientSecret)
        if (confirmError) {
          sponsorshipError.value = `Sponsorship authentication failed: ${confirmError.message}.` +
            (donationSuccess.value ? ' Your donation was still processed.' : '')
        } else {
          sponsorshipSuccess.value = true
        }
      } else {
        sponsorshipError.value = (sponsorshipResponse.msg || 'Sponsorship setup failed.') +
          (donationSuccess.value ? ' Your donation was still processed.' : '')
      }
    }
  } catch (error) {
    console.error('Support processing error:', error)
    donationError.value = 'An error occurred. Please try again.'
  } finally {
    processing.value = false
  }
}

// Mount the card field only once the visitor has opted into paying for something
watch(needsPayment, async (value) => {
  if (value) {
    await initStripe()
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;

.main-card {
  @include main-card;
}

.section-title {
  @include section-header;
}

.support-actions {
  display: flex;
  flex-wrap: wrap;
}

.preset-btn {
  min-width: 72px;
}

.stripe-card-element {
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  padding: 12px;
  background: #fff;
}
</style>
